import type { CampaignApiQueryValue } from '@/lib/api/campaign-api-client';
import { FILTERABLE_FIELDS, type FilterFieldConfig, type FilterValue } from '../config/filterable-fields';
import type { FilterRow } from '../components/filter-row-builder/filter-row-builder';
import type { ContactsFilters } from '../types/contacts-filters.types';
import type { ContactsRestFilters } from '../types/contacts-api.types';

// Re-exported so the lists module and the hooks keep importing the filter type from the same place
// they import the builders, rather than reaching into types/ separately.
export type { ContactsRestFilters };

export type BuildContactsRestFilterParams = {
  emailStatusFilters?: string[];
  smsStatusFilters?: string[];
  sourceFilters?: string[];
  listId?: string;
  fieldFilters?: ContactsFilters;
  advancedRows?: FilterRow[];
  registry?: FilterFieldConfig[];
};

const isSuppressedFilterValue = (value: string): boolean =>
  value.trim().toLowerCase() === 'suppressed';

/**
 * Splits UI email-status chips so "Suppressed" drives `?suppressed=true` instead of
 * `EmailStatus=suppressed` (which the API rejects with 400).
 */
export const partitionEmailStatusFilters = (
  emailStatusFilters: string[] = []
): { emailStatusFilters: string[]; suppressed?: true } => {
  const statuses: string[] = [];
  let suppressed = false;

  for (const value of emailStatusFilters) {
    if (isSuppressedFilterValue(value)) {
      suppressed = true;
      continue;
    }
    statuses.push(value);
  }

  return suppressed
    ? { emailStatusFilters: statuses, suppressed: true }
    : { emailStatusFilters: statuses };
};

const toRestFilterKey = (fieldName: string): string =>
  fieldName.charAt(0).toLowerCase() + fieldName.slice(1);

const getRestFilterKey = (field: FilterFieldConfig): string =>
  field.storageKind === 'customAttribute' ? field.key : toRestFilterKey(field.mongoField);

const joinFilterValues = (values: string[]): string =>
  values.map((value) => value.trim()).filter(Boolean).join(',');

const toRestSubscriptionStatus = (value: string): string => value.trim().toLowerCase();

/** Trimmed, de-duped, empties dropped. Statuses are additionally lowercased — the backend matches
 * them exactly against `subscribed|unsubscribed|pending` and 400s on anything else. */
const toValues = (values: string[], lowercase: boolean): string[] =>
  Array.from(
    new Set(values.map((v) => (lowercase ? toRestSubscriptionStatus(v) : v.trim())).filter(Boolean))
  );

/**
 * Rest keys that map to a first-class repeated query param rather than a `filter[...]` entry.
 *
 * `source` is deliberately absent. `GET /v1/lists/{id}/contacts` accepts a `Source` param but
 * `GET /v1/contacts` — which is where this builder's output goes — does not, and ASP.NET silently
 * ignores an unknown query param. Promoting it would turn a Source filter into "no filter at all"
 * and quietly return every contact. Left in the `filter[...]` map, it keeps exactly the wire
 * format it has today.
 */
const FIRST_CLASS_KEYS: Record<string, 'emailStatus' | 'smsStatus' | 'lists'> = {
  emailSubscriptionStatus: 'emailStatus',
  smsSubscriptionStatus: 'smsStatus',
  listId: 'lists',
};

/**
 * The only per-channel status values `GET /v1/contacts` accepts. `GetContactsQueryValidator`
 * rejects anything else with a `400`. UI "Suppressed" is not a subscription status — it is
 * stripped by `partitionEmailStatusFilters` and sent as `?suppressed=true` instead. Unknown
 * status values that still reach this builder are dropped (not encoded as EmailStatus).
 */
const API_STATUS_VALUES = new Set(['subscribed', 'unsubscribed', 'pending']);

const isRangeValue = (value: unknown): value is { from?: string; to?: string } =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && ('from' in value || 'to' in value);

const isFieldValueActive = (field: FilterFieldConfig, value: FilterValue | undefined): boolean => {
  if (value === undefined || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  if (field.defaultValue !== undefined) {
    return JSON.stringify(value) !== JSON.stringify(field.defaultValue);
  }
  if (isRangeValue(value)) return Boolean(value.from || value.to);
  return true;
};

const formatFieldFilterValue = (value: FilterValue): string => {
  if (Array.isArray(value)) {
    return joinFilterValues(value.map(String));
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (isRangeValue(value)) {
    return joinFilterValues([value.from ?? '', value.to ?? '']);
  }
  return String(value).trim();
};

/** Date inputs are `YYYY-MM-DD`; REST range bounds need full-day UTC ISO. */
const toDateRangeBound = (value: string, bound: 'from' | 'to'): string => {
  const day = value.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return value.trim();
  return bound === 'from' ? `${day}T00:00:00Z` : `${day}T23:59:59Z`;
};

const formatAttributeRangeBounds = (
  field: FilterFieldConfig,
  value: { from?: string; to?: string }
): { from?: string; to?: string } => {
  const fromRaw = value.from?.trim() ?? '';
  const toRaw = value.to?.trim() ?? '';
  const isDate = field.valueType === 'date' || field.operators.includes('dateRange');

  return {
    ...(fromRaw
      ? { from: isDate ? toDateRangeBound(fromRaw, 'from') : fromRaw }
      : {}),
    ...(toRaw ? { to: isDate ? toDateRangeBound(toRaw, 'to') : toRaw } : {}),
  };
};

const addAttributeRange = (
  filters: ContactsRestFilters,
  key: string,
  range: { from?: string; to?: string }
): void => {
  if (!range.from && !range.to) return;
  filters.attributeRanges = { ...(filters.attributeRanges ?? {}), [key]: range };
};

/**
 * Adds one resolved rest key/value into the structured filters, routing first-class keys away from
 * the `filter[...]` map. First-class values are appended rather than overwritten, so two UI
 * controls targeting the same field widen the filter instead of one silently winning.
 */
const addRestFilterValue = (filters: ContactsRestFilters, key: string, value: string): void => {
  const trimmed = value.trim();
  if (!trimmed) return;

  const firstClass = FIRST_CLASS_KEYS[key];
  if (firstClass) {
    const isStatus = firstClass === 'emailStatus' || firstClass === 'smsStatus';
    // One control can still hold several values (a multiSelect, or a comma-typed advanced row), so
    // split before storing rather than keeping one comma-joined string the backend can't match.
    const incoming = toValues(trimmed.split(','), isStatus);
    if (incoming.length === 0) return;

    const accepted = isStatus ? incoming.filter((v) => API_STATUS_VALUES.has(v)) : incoming;

    if (accepted.length > 0) {
      filters[firstClass] = Array.from(new Set([...(filters[firstClass] ?? []), ...accepted]));
    }

    // Drop API-unknown status values (e.g. UI "Suppressed") — those drive `?suppressed=` via
    // `partitionEmailStatusFilters`, not EmailStatus / filter[emailSubscriptionStatus].
    return;
  }

  // Genuine custom attribute, one entry per key. A multienum's comma list is sent as-is: the backend
  // reads it as any-of (#231). Every other type compares the single value with `==`.
  filters.attributes = { ...(filters.attributes ?? {}), [key]: trimmed };
};

/**
 * Serializes structured contact filters onto a query object for `campaignApiGet`.
 * First-class filters become repeated keys; equality attributes become `Filter[key]=value`;
 * ranges become `Filter[key][from]` / `Filter[key][to]`.
 *
 * Shared by `GET /v1/contacts` and `GET /v1/lists/{id}/contacts` — those two accept the same query
 * string for the same narrowing (`GetContactsQuery` and `GetListMembersQuery` mirror each other
 * server-side), so they must not grow separate serializers. `lists` is the one exception: it is
 * meaningless on the list-members endpoint, and no caller of that endpoint sets it.
 */
export const appendRestFilterQuery = (
  query: Record<string, CampaignApiQueryValue>,
  filter: ContactsRestFilters = {}
): Record<string, CampaignApiQueryValue> => {
  const result: Record<string, CampaignApiQueryValue> = { ...query };

  if (filter.emailStatus?.length) result.EmailStatus = filter.emailStatus;
  if (filter.smsStatus?.length) result.SmsStatus = filter.smsStatus;
  if (filter.source?.length) result.Source = filter.source;
  if (filter.lists?.length) result.Lists = filter.lists;

  for (const [key, value] of Object.entries(filter.attributes ?? {})) {
    result[`Filter[${key}]`] = value;
  }

  for (const [key, range] of Object.entries(filter.attributeRanges ?? {})) {
    if (range.from) result[`Filter[${key}][from]`] = range.from;
    if (range.to) result[`Filter[${key}][to]`] = range.to;
  }

  return result;
};

export const buildContactsRestFilterValues = (
  params: BuildContactsRestFilterParams
): ContactsRestFilters => {
  const result: ContactsRestFilters = {};

  if (params.emailStatusFilters?.length) {
    addRestFilterValue(result, 'emailSubscriptionStatus', params.emailStatusFilters.join(','));
  }

  if (params.smsStatusFilters?.length) {
    addRestFilterValue(result, 'smsSubscriptionStatus', params.smsStatusFilters.join(','));
  }

  if (params.sourceFilters?.length) {
    addRestFilterValue(result, 'source', params.sourceFilters.join(','));
  }

  const listId = params.listId?.trim();
  if (listId) {
    addRestFilterValue(result, 'listId', listId);
  }

  const registry = params.registry ?? FILTERABLE_FIELDS;

  if (params.fieldFilters) {
    for (const field of registry) {
      const value = params.fieldFilters.fields[field.key];
      if (!isFieldValueActive(field, value)) continue;

      const restKey = getRestFilterKey(field);
      const isCustomRange =
        field.storageKind === 'customAttribute' &&
        (field.operators.includes('numberRange') || field.operators.includes('dateRange'));

      if (isCustomRange && isRangeValue(value)) {
        addAttributeRange(result, restKey, formatAttributeRangeBounds(field, value));
        continue;
      }

      addRestFilterValue(result, restKey, formatFieldFilterValue(value));
    }
  }

  if (params.advancedRows?.length) {
    for (const row of params.advancedRows) {
      // An incomplete row (no field or no operator chosen yet) contributes nothing.
      if (!row.fieldKey || !row.operator) continue;

      // `isEmpty` and `notEquals` have no wire encoding: the API's filter[...] clause is an
      // equality match, so neither "is empty" nor negation can be expressed. Dropping them here
      // means such a row is ignored rather than silently inverted into its opposite.
      if (row.operator === 'isEmpty' || row.operator === 'notEquals') continue;

      // Every remaining operator compares against a value, so a blank one is not a filter. The
      // `row.operator !== 'isEmpty'` guard this replaces was dead: the line above already
      // `continue`s on 'isEmpty', so the comparison was always true and TypeScript flagged it
      // (TS2367 — no overlap between the narrowed operator union and '"isEmpty"').
      if (!row.value.trim()) continue;

      const field = registry.find((entry) => entry.key === row.fieldKey);
      if (!field) continue;

      const restKey = getRestFilterKey(field);

      if (
        field.storageKind === 'customAttribute' &&
        (row.operator === 'dateRange' || row.operator === 'numberRange')
      ) {
        try {
          const parsed: unknown = JSON.parse(row.value);
          let range: { from?: string; to?: string } | undefined;

          if (isRangeValue(parsed)) {
            range = { from: parsed.from, to: parsed.to };
          } else if (Array.isArray(parsed) && parsed.length === 2) {
            const [min, max] = parsed;
            range = {
              ...(min !== undefined && min !== null && String(min).trim()
                ? { from: String(min) }
                : {}),
              ...(max !== undefined && max !== null && String(max).trim()
                ? { to: String(max) }
                : {}),
            };
          } else if (typeof parsed === 'object' && parsed !== null) {
            const record = parsed as { min?: unknown; max?: unknown };
            const min = record.min;
            const max = record.max;
            range = {
              ...(typeof min === 'string' || typeof min === 'number'
                ? { from: String(min) }
                : {}),
              ...(typeof max === 'string' || typeof max === 'number' ? { to: String(max) } : {}),
            };
          }

          if (range && (range.from || range.to)) {
            addAttributeRange(result, restKey, formatAttributeRangeBounds(field, range));
          }
        } catch {
          // Malformed range JSON — skip rather than sending a value the API rejects.
        }
        continue;
      }

      addRestFilterValue(result, restKey, row.value.trim());
    }
  }

  return result;
};
